import { Buffer } from 'buffer';
import { inflate } from 'pako';

type EntityType = 'AD_ACCOUNT' | 'CREATIVE_LIBRARY' | 'MARKETER_ORGANIZATION' | 'PUBLISHER_ORGANIZATION';

export type DAAPIRolesV4 = {
  ['entity-id']: string;
  ['entity-type']: EntityType;
  role: {
    name: string;
    permissions: string[] | null;
  };
};

export type Token = {
  //decoded roles
  user_role_v4: string;
  sub: string;
  ams_user_id?: string;
  // When truthy, the account-manager service excluded high-volume role
  // assignments (e.g. SELLER) from user_role_v4 to keep the JWT under the
  // 8KB header limit. Consumers must not rely on user_role_v4 alone to
  // determine whether the user is a seller when this is set; the full
  // role list must be fetched via the getRoleClaims GQL query.
  //
  // Cognito custom claims arrive as strings, so production tokens send
  // "true"/"false". Use isRolesExcluded() to read this safely.
  roles_excluded?: boolean | string;
};

// Cognito custom claims are stringified, so a token may carry "true" / "false"
// rather than the boolean values. Normalize so callers can branch consistently.
export const isRolesExcluded = (token: Pick<Token, 'roles_excluded'>): boolean => {
  return token.roles_excluded === true || token.roles_excluded === 'true';
};

// Decompresses the base64-encoded role string used in both JWT user_role_v4
// claims and the getRoleClaims GQL response.
//
// IMPORTANT: despite "gzip" terminology that appears in some backend code and
// docs, the wire format is actually a zlib stream (RFC 1950 deflate), not
// GZIP (RFC 1952). The backend produces it via DeflaterOutputStream, not
// GZIPOutputStream — strict GZIP decoders reject it with "Not in GZIP format".
// We use pako.inflate (zlib decoder) here; pako.ungzip would also appear to
// work because pako is lenient, but inflate is the correct function for the
// actual wire format.
export const decompressRoles = (compressedRoles: string): DAAPIRolesV4[] => {
  const charData = Uint8Array.from(Buffer.from(compressedRoles, 'base64'));
  const decompressed = inflate(charData, { to: 'string' });

  return JSON.parse(decompressed);
};

// Determines whether the user is a publisher (PUBLISHER_ORGANIZATION member or SELLER)
// from an already-decompressed role list. Use this when roles_excluded is true
// and roles have been fetched via getRoleClaims.
export const checkIsPublisherUserFromRoles = (roles: DAAPIRolesV4[]): boolean => {
  return roles.some(
    (parsedRole) => parsedRole['entity-type'] === 'PUBLISHER_ORGANIZATION' || parsedRole.role.name === 'SELLER'
  );
};

// Checks to see if a user is either a PUBLISHER_ORGANIZATION user or SELLER user.
// Note: when decodedToken.roles_excluded is true, SELLER assignments are NOT
// present in user_role_v4. Callers that need a correct answer in that case must
// fetch the full role list via getRoleClaims and use checkIsPublisherUserFromRoles.
export const checkIsPublisherUser = (decodedToken: Token): boolean | null => {
  try {
    return checkIsPublisherUserFromRoles(decompressRoles(decodedToken.user_role_v4));
  } catch (error) {
    console.error(error);
    return null;
  }
};

// Checks to see if a user is a PUBLISHER_ORGANIZATION user only (not a SELLER user).
// PUBLISHER_ORGANIZATION assignments are never excluded from user_role_v4, so this
// remains correct regardless of the roles_excluded flag.
export const checkIsPublisherOrganizationOnlyUser = (decodedToken: Token): boolean | null => {
  try {
    return decompressRoles(decodedToken.user_role_v4).some(
      (parsedRole) => parsedRole['entity-type'] === 'PUBLISHER_ORGANIZATION'
    );
  } catch (error) {
    console.error(error);
    return null;
  }
};
